/**
 * Pure node-tree manipulation helpers — no React, no DOM, so these are
 * directly unit-testable (see treeHelpers.test.js) and reused by both the
 * click-to-add flow (Phase 5) and the drag-and-drop flow (Phase 6).
 * `nodes` is always the ROOT-level array; the sentinel id 'root' refers to
 * that array itself (there's no real node with id 'root').
 */

export const ROOT_ID = 'root';

export function findNode(nodes, id) {
    for (const node of nodes) {
        if (node.id === id) return node;
        if (node.children) {
            const found = findNode(node.children, id);
            if (found) return found;
        }
    }
    return null;
}

export function insertNode(nodes, parentId, newNode) {
    if (!parentId || parentId === ROOT_ID) return [...nodes, newNode];
    return nodes.map((node) => {
        if (node.id === parentId) {
            return { ...node, children: [...(node.children || []), newNode] };
        }
        if (node.children) {
            return { ...node, children: insertNode(node.children, parentId, newNode) };
        }
        return node;
    });
}

/** Inserts newNode into parentId's children at a specific index (append if index is omitted/out of range). */
export function insertNodeAt(nodes, parentId, newNode, index) {
    const insertInto = (arr) => {
        if (index === undefined || index === null || index < 0 || index > arr.length) {
            return [...arr, newNode];
        }
        return [...arr.slice(0, index), newNode, ...arr.slice(index)];
    };
    if (!parentId || parentId === ROOT_ID) return insertInto(nodes);
    return nodes.map((node) => {
        if (node.id === parentId) {
            return { ...node, children: insertInto(node.children || []) };
        }
        if (node.children) {
            return { ...node, children: insertNodeAt(node.children, parentId, newNode, index) };
        }
        return node;
    });
}

export function removeNode(nodes, id) {
    return nodes
        .filter((node) => node.id !== id)
        .map((node) => (node.children ? { ...node, children: removeNode(node.children, id) } : node));
}

export function updateNodeProps(nodes, id, key, value) {
    return nodes.map((node) => {
        if (node.id === id) {
            return { ...node, props: { ...node.props, [key]: value } };
        }
        if (node.children) {
            return { ...node, children: updateNodeProps(node.children, id, key, value) };
        }
        return node;
    });
}

/**
 * Replaces a background node's image layers. Also clears the legacy
 * `props.src` in the same update — getBackgroundLayers() falls back to it
 * when there are no layers, so leaving it behind would resurrect a removed
 * image the moment the last layer is deleted. An empty list drops the
 * `layers` key entirely.
 */
export function updateNodeLayers(nodes, id, layers) {
    return nodes.map((node) => {
        if (node.id === id) {
            const { layers: _old, ...rest } = node;
            const next = { ...rest, props: { ...node.props, src: '' } };
            if (layers && layers.length > 0) next.layers = layers;
            return next;
        }
        if (node.children) {
            return { ...node, children: updateNodeLayers(node.children, id, layers) };
        }
        return node;
    });
}

/**
 * Sets one CSS property within a node's style.<breakpoint> bucket. An
 * empty-string value REMOVES the property entirely (rather than storing
 * `''`) so a cleared field doesn't linger as a meaningless empty
 * declaration once it reaches buildResponsiveCss() on the guest side.
 */
export function updateNodeStyle(nodes, id, breakpoint, cssProp, value) {
    return nodes.map((node) => {
        if (node.id === id) {
            const bucket = { ...(node.style?.[breakpoint] || {}) };
            if (value === '' || value === undefined || value === null) {
                delete bucket[cssProp];
            } else {
                bucket[cssProp] = value;
            }
            return { ...node, style: { ...node.style, [breakpoint]: bucket } };
        }
        if (node.children) {
            return { ...node, children: updateNodeStyle(node.children, id, breakpoint, cssProp, value) };
        }
        return node;
    });
}

/**
 * Sets SEVERAL CSS properties in one node's style.<breakpoint> bucket as one
 * update (so it is one undo step) — used when a single gesture changes more
 * than one property, e.g. dragging a floating component sets left AND top and
 * clears right/bottom. As in updateNodeStyle, an empty value removes the key.
 */
export function updateNodeStyles(nodes, id, breakpoint, patch) {
    return nodes.map((node) => {
        if (node.id === id) {
            const bucket = { ...(node.style?.[breakpoint] || {}) };
            for (const [cssProp, value] of Object.entries(patch)) {
                if (value === '' || value === undefined || value === null) delete bucket[cssProp];
                else bucket[cssProp] = value;
            }
            return { ...node, style: { ...node.style, [breakpoint]: bucket } };
        }
        if (node.children) {
            return { ...node, children: updateNodeStyles(node.children, id, breakpoint, patch) };
        }
        return node;
    });
}

/**
 * Returns the id of nodeId's immediate parent container, or ROOT_ID if
 * it's a top-level node, or null if nodeId doesn't exist in the tree.
 */
export function findParentContainerId(nodes, nodeId, parentId = ROOT_ID) {
    for (const node of nodes) {
        if (node.id === nodeId) return parentId;
        if (node.children) {
            const found = findParentContainerId(node.children, nodeId, node.id);
            if (found !== null) return found;
        }
    }
    return null;
}

/**
 * True if candidateId is ancestorId itself, or anywhere in ancestorId's
 * own subtree — used to reject an invalid drop (moving a container into
 * itself or one of its own descendants), mirroring the server-side
 * cycle/depth guard in backend/utils/landingPageSchema.js as
 * defense-in-depth (the server re-validates independently regardless).
 */
export function isSelfOrDescendant(nodes, ancestorId, candidateId) {
    if (ancestorId === candidateId) return true;
    const ancestor = findNode(nodes, ancestorId);
    if (!ancestor || !ancestor.children) return false;
    // findNode already recurses through the entire subtree given to it, so
    // searching ancestor.children alone covers every depth below ancestor.
    return !!findNode(ancestor.children, candidateId);
}

/**
 * Moves an existing node to a new parent container at a given index.
 * No-op (returns the original tree reference) if the move is invalid
 * (node not found, target is the node itself or its own descendant).
 */
export function moveNode(nodes, nodeId, targetParentId, targetIndex) {
    const node = findNode(nodes, nodeId);
    if (!node) return nodes;
    if (isSelfOrDescendant(nodes, nodeId, targetParentId)) return nodes;

    const withoutNode = removeNode(nodes, nodeId);
    return insertNodeAt(withoutNode, targetParentId, node, targetIndex);
}

/** Returns the children array a given parent id currently has (root or a container node). */
export function getChildrenOf(nodes, parentId) {
    if (!parentId || parentId === ROOT_ID) return nodes;
    const node = findNode(nodes, parentId);
    return node?.children || [];
}

/**
 * Moves an existing node so it sits immediately after `afterNodeId` within
 * `targetParentId`'s children. Unlike moveNode's raw numeric index, this
 * resolves the insertion point from afterNodeId's position AFTER the
 * dragged node has already been removed — avoiding an off-by-one when the
 * dragged node was earlier in the SAME list as afterNodeId (removing it
 * first shifts every later sibling's index down by one; resolving
 * afterNodeId's position post-removal sidesteps that entirely instead of
 * requiring the caller to account for the shift).
 *
 * afterNodeId semantics:
 *   - a real node id  -> insert immediately after that sibling
 *   - null            -> insert at the very START of targetParentId's children
 *   - undefined       -> APPEND at the very END of targetParentId's children
 *   (matches insertNodeAt's own undefined-means-append convention)
 */
export function moveNodeAfter(nodes, nodeId, targetParentId, afterNodeId) {
    const node = findNode(nodes, nodeId);
    if (!node) return nodes;
    if (isSelfOrDescendant(nodes, nodeId, targetParentId)) return nodes;
    if (nodeId === afterNodeId) return nodes;

    const withoutNode = removeNode(nodes, nodeId);
    if (afterNodeId === undefined) {
        return insertNodeAt(withoutNode, targetParentId, node, undefined);
    }
    if (afterNodeId === null) {
        return insertNodeAt(withoutNode, targetParentId, node, 0);
    }
    const siblings = getChildrenOf(withoutNode, targetParentId);
    const afterIdx = siblings.findIndex((n) => n.id === afterNodeId);
    const resolvedIndex = afterIdx === -1 ? undefined : afterIdx + 1;
    return insertNodeAt(withoutNode, targetParentId, node, resolvedIndex);
}
